import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import type { TGetIntentionsPayload } from '@/src/features/intention/api/types/getIntentionsApi.types'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { getIntentionsQueryOptions, useIntentionsQuery } from '@/src/features/intention/services/useIntentionsQuery'
import { getShuffledIntentions } from '@/src/features/intention/utils/getShuffledIntentions'
import { ICON } from '@/src/general/enums/icons'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS, type TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useTranslate } from '@/src/lang/useTranslate'

import {
  getIntentionsByTypeQueryOptions,
  useIntentionsByTypeQuery,
} from '../../../../services/useIntentionsByTypeQuery'
import { useMarkIntentionsAsViewedMutation } from '../../../../services/useMarkIntentionsAsViewedMutation'
import { Intention } from './Intention'
import { IntentionPlaceholder } from './IntentionPlaceholder'

type TIntentionsListProps = {
  trackingSource: TRACKING_SOURCE
  landingPageSlug?: string
  showTitle?: boolean
}

export const IntentionsList = ({
  showTitle = true,
  trackingSource,
  landingPageSlug,
}: TIntentionsListProps): ReactNode => {
  // instance of Services
  // Local State
  const [level, setLevel] = useState(0)
  const [exceptIntentionsIds, setExceptIntentionsIds] = useState<number[]>([])
  const [isMarkedAsView, setIsMarkedAsView] = useState(false)
  const { t } = useTranslate()
  const queryClient = useQueryClient()

  const intentionsQueryPayload: TGetIntentionsPayload = { exceptIntentionsIds, level, landingPageSlug }

  // Queries
  const { intentions, isLoading: intentionsLoading } = useIntentionsQuery(intentionsQueryPayload)
  const { mutate: markIntentionsAsViewed } = useMarkIntentionsAsViewedMutation()
  const { intentions: arrivalIntentions = [], isLoading: arrivalIntentionsLoading } = useIntentionsByTypeQuery(
    landingPageSlug == null,
    INTENTION_TYPE.ARRIVAL,
  )
  const { intentions: birthdayIntentions = [], isLoading: birthdayIntentionsLoading } = useIntentionsByTypeQuery(
    landingPageSlug == null,
    INTENTION_TYPE.BIRTHDAY,
  )

  // Computed State
  const isPending = !!intentionsLoading || !!arrivalIntentionsLoading || !!birthdayIntentionsLoading

  const allIntentions = getShuffledIntentions({
    [INTENTION_TYPE.INTENTION]: intentions ?? [],
    [INTENTION_TYPE.ARRIVAL]: arrivalIntentions,
    [INTENTION_TYPE.BIRTHDAY]: birthdayIntentions,
  })

  // Life cycle effect
  useEffect(() => {
    if (isMarkedAsView) return
    const time = setTimeout(() => {
      markIntentionsAsViewed(intentions?.map(({ id }) => id) ?? [])
      setIsMarkedAsView(true)
    }, 2000)

    return () => clearTimeout(time)
  }, [intentions, markIntentionsAsViewed, isMarkedAsView])

  // Event handlers
  const handleIntentionRefresh = () => {
    GTM.sendEvent({ event: EVENTS.INTENTIONS_SHUFFLE })

    setExceptIntentionsIds(intentions?.map(({ id }) => id) ?? [])
    setIsMarkedAsView(false)
    setLevel((prev) => Math.min(prev + 1, 3))

    queryClient.removeQueries({ queryKey: getIntentionsQueryOptions(intentionsQueryPayload, queryClient).queryKey })
    queryClient.removeQueries({
      queryKey: getIntentionsByTypeQueryOptions(landingPageSlug == null, INTENTION_TYPE.ARRIVAL, null).queryKey,
    })
    queryClient.removeQueries({
      queryKey: getIntentionsByTypeQueryOptions(landingPageSlug == null, INTENTION_TYPE.BIRTHDAY, null).queryKey,
    })
  }

  return (
    <View className="gap-4">
      {showTitle && (
        <View className="self-start px-4 pt-8">
          <Typography size="lg" heading="h2" contrast="low" className="font-black">
            {t('word.intentions')}
          </Typography>
        </View>
      )}
      <View className="flex w-full flex-col gap-4 self-start">
        {allIntentions?.map((intention) => (
          <Intention
            key={intention.id}
            type={intention.type}
            intention={intention}
            intentionsQueryPayload={intentionsQueryPayload}
            trackingSource={trackingSource}
          />
        ))}
        {!!isPending &&
          Array.from({ length: 5 - allIntentions.length }, (_, index) => (
            <IntentionPlaceholder key={`placeholder-${index}`} />
          ))}
      </View>
      <Button
        colors="primary"
        variant="light"
        startIcon={ICON.REFRESH}
        onPress={handleIntentionRefresh}
        className="my-4"
      >
        {t('intention.reload')}
      </Button>
      <Spacer />
    </View>
  )
}
